import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api, setCsrfToken } from './api';

export type AuthUser = {
  id: string;
  email: string;
  role: 'admin' | 'reviewer' | 'dock_manager' | 'viewer';
  dock_scope_id?: string | null;
};

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: AuthUser };

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  // Boot: ask the server who we are. 401 → anonymous.
  useEffect(() => {
    let cancelled = false;
    api<{ user: AuthUser; csrf_token: string }>('/api/auth/me')
      .then((data) => {
        if (cancelled) return;
        setCsrfToken(data.csrf_token);
        setState({ status: 'authenticated', user: data.user });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setCsrfToken(null);
          setState({ status: 'anonymous' });
        } else {
          setCsrfToken(null);
          setState({ status: 'anonymous' });
        }
      });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api<{ user: AuthUser; csrf_token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setCsrfToken(data.csrf_token);
    setState({ status: 'authenticated', user: data.user });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      setCsrfToken(null);
      setState({ status: 'anonymous' });
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ ...state, login, logout }), [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
